// components/StudioModelsPopover.tsx
//
// Awen-style "Models" popover for the STUDIO chat composer's + button: title + routing
// explainer, segmented modality tabs (image / video) with activity dots, and rows of
// generative models (provider icon + name + resolution or version chips + NEW badge). Selecting a model
// PINS it for the project's generations (sets kernel `diffusionModel` + the existing
// `lockSelectedModelForAgent` preference so the director's SELECT_MODEL is blocked);
// clicking the pinned model again returns to Auto (the router picks per task). A chip pins
// the model AND sets its resolution parameter.
//
// Rendering: the panel is PORTALED to document.body and positioned fixed above the anchor,
// the composer card clips overflow, so an in-place absolute panel gets cut (screenshot-proven).
// The pure `StudioModelsPopoverView` carries all the visuals so the dev-preview route can
// render it kernel-free for headless Chrome verification.
'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Film, Image as ImageIcon, Sparkles, X } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { STUDIO_MODEL_REGISTRY, compareStudioModels, getActiveSelectableStudioModelIds, buildStudioFrontendModelAccessState, isStudioModelSupportedForProjectType } from '#lib/model-registry';
import { clientAccessSuspension } from '#lib/access-suspension';
import { getDefaultParamsForModel } from '#lib/models';
import { QWEN_IMAGE_21_RESOLUTION_OPTIONS } from '#lib/qwen-image-21';
import { studioModelFamilyOf, visibleFamilyVersions, type StudioModelFamily } from '#lib/studioModelFamilies';
import { resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';

export type StudioModelsModality = 'image' | 'video';

const MODALITY_OF_CATEGORY: Record<string, StudioModelsModality | null> = {
  Generate: 'image',
  Edit: 'image',
  Video: 'video',
};

const NANO_IMAGE_SIZE_CHIPS = { paramId: 'imageSize', choices: [{ label: '1K', value: '1K' }, { label: '2K', value: '2K' }, { label: '4K', value: '4K' }] };

/** Discrete resolution choices per model (label shown on the chip, value written to the param). */
const RESOLUTION_CHIPS: Record<string, { paramId: string; choices: Array<{ label: string; value: string | number }> }> = {
  'nano-banana-2': NANO_IMAGE_SIZE_CHIPS,
  'nano-banana-2.1': NANO_IMAGE_SIZE_CHIPS,
  'nano-banana-pro': NANO_IMAGE_SIZE_CHIPS,
  'seedream4.5': { paramId: 'outputMegaPixels', choices: [{ label: '2K', value: 4 }, { label: '4K', value: 16 }] },
  'qwen-image-2.1': { paramId: 'resolution', choices: QWEN_IMAGE_21_RESOLUTION_OPTIONS.map((value) => ({ label: value, value })) },
  'kling-3': { paramId: 'resolution', choices: [{ label: '720p', value: '720p' }, { label: '1080p', value: '1080p' }] },
  'kling-3-omni': { paramId: 'resolution', choices: [{ label: '720p', value: '720p' }, { label: '1080p', value: '1080p' }] },
  'seedance-2.0': { paramId: 'resolution', choices: [{ label: '480p', value: '480p' }, { label: '720p', value: '720p' }, { label: '1080p', value: '1080p' }, { label: '4K', value: '4k' }] },
  'seedance-2.5': { paramId: 'resolution', choices: [{ label: '480p', value: '480p' }, { label: '720p', value: '720p' }] },
  'flux-3': { paramId: 'resolution', choices: [{ label: '720p', value: '720p' }, { label: '1080p', value: '1080p' }] },
};

// Every model shows its REAL provider icon, in color (Awen-style). Files live in
// /public/providers (official favicons). Resolution order: per-model override (Nano Banana
// is Google DeepMind's, distinct from Gemini) -> the registry's provider field -> sparkle.
const MODEL_ICON_OVERRIDES: Record<string, string> = {
  'nano-banana': '/providers/deepmind.png',
  'nano-banana-pro': '/providers/deepmind.png',
  'nano-banana-2': '/providers/deepmind.png',
  'nano-banana-2.1': '/providers/deepmind.png',
  'topaz-image-enhance': '/providers/topaz.png',
  'topaz-video-upscale': '/providers/topaz.png',
  'mirelo-sfx-1.6': '/providers/mirelo.svg',
  // The video models we run on fal: fal only hosts them, so the row must
  // show the lab that built the model. Without these they all fall through to the sparkle,
  // because the registry's provider field says 'fal' and there is no fal entry below.
  'veo-3.1-fast': '/providers/deepmind.png',
  'ltx-2.3-fast': '/providers/ltx.svg',
  'kling-2.5-turbo': '/providers/kling.png',
  'luma-ray-2-flash': '/providers/luma.png',
  'hailuo-02-fast': '/providers/hailuo.png',
  // wan.png is Wan's own org mark and looks almost identical to qwen.png on purpose:
  // Alibaba ships one Tongyi mark across Qwen and Wan. Kept as its own file so the two can
  // diverge later without touching call sites. Do not "dedupe" them.
  'wan-2.2-i2v': '/providers/wan.png',
  'wan-2.2-fast': '/providers/wan.png',
  'minimax-h3-max': '/providers/hailuo.png',
  'minimax-h3-max-turbo': '/providers/hailuo.png',
  'wan-3.0-prime': '/providers/wan.png',
  'grok-imagine-video-1.5': '/providers/xai.png',
  'kling-3-turbo': '/providers/kling.png',
};
const PROVIDER_ICON: Record<string, string> = {
  'azure-openai': '/providers/openai.svg',
  openai: '/providers/openai.svg',
  gemini: '/providers/deepmind.png',
  bfl: '/providers/bfl.png',
  qwen: '/providers/qwen.png',
  xai: '/providers/xai.png',
  seed: '/providers/seed.png',
  bytedance: '/providers/seed.png',
  ideogram: '/providers/ideogram.png',
  kling: '/providers/kling.png',
  replicate: '/providers/replicate.png',
  magnific: '/providers/magnific.png',
};

const INVERT_TO_WHITE_STYLE: React.CSSProperties = { filter: 'brightness(0) invert(1)' };

const ProviderIcon: React.FC<{ modelId: string; provider?: string }> = React.memo(({ modelId, provider }) => {
  const src = MODEL_ICON_OVERRIDES[modelId] ?? (provider ? PROVIDER_ICON[provider] : undefined);
  // Track WHICH src failed rather than a bare boolean: rows are reused across modality tab
  // switches, so a sticky flag would suppress a later model's logo after one 404.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const handleError = useCallback(() => setFailedSrc(src ?? null), [src]);
  if (src && failedSrc !== src) {
    // The OpenAI mark is black, render it white on the dark panel.
    const invertToWhite = src.includes('openai');
    return (
      <img
        src={src}
        alt=""
        className="h-4 w-4 shrink-0 rounded-[3px] object-contain"
        style={invertToWhite ? INVERT_TO_WHITE_STYLE : undefined}
        loading="lazy"
        onError={handleError}
      />
    );
  }
  return <Sparkles size={15} strokeWidth={1.8} className="shrink-0 text-[#B5B5B5]" />;
});
ProviderIcon.displayName = 'ProviderIcon';

interface RegistryEntryLike {
  name?: string;
  provider?: string;
  uiCategory?: string;
  showInModelSelector?: boolean;
  comparisonProfile?: { lifecycle?: string };
}

export interface StudioModelRow {
  id: string;
  name: string;
  provider?: string;
  modality: StudioModelsModality;
  isNew: boolean;
  versions?: ReadonlyArray<{ id: string; label: string; name: string }>;
}

export const rowHoldsModel = (row: StudioModelRow, modelId: string | null): boolean =>
  row.versions ? row.versions.some((version) => version.id === modelId) : row.id === modelId;

// A family row's id is the version its name pins when none of its versions is pinned.
const groupFamilies = (rows: StudioModelRow[]): StudioModelRow[] => {
  const ids = rows.map((row) => row.id);
  const grouped = new Set<StudioModelFamily>();
  return rows.flatMap((row) => {
    const family = studioModelFamilyOf(row.id);
    if (!family) return [row];
    if (grouped.has(family)) return [];
    grouped.add(family);
    // A version chip is labelled with its model's full name: that is the name the Daimon is told to click.
    const versions = visibleFamilyVersions(family, ids).map((version) => ({ ...version, name: rows.find((candidate) => candidate.id === version.id)?.name ?? version.label }));
    return [{ ...row, id: versions[0]?.id ?? row.id, name: family.name, versions }];
  });
};

export const buildStudioModelRows = (
  allowedIds?: Set<string> | null,
  projectType?: string | null,
): Record<StudioModelsModality, StudioModelRow[]> => {
  const rows: Record<StudioModelsModality, StudioModelRow[]> = { image: [], video: [] };
  const compareModels = compareStudioModels();
  const entries = Object.entries(STUDIO_MODEL_REGISTRY as Record<string, RegistryEntryLike>).sort(([a], [b]) => compareModels(a, b));
  for (const [id, defRaw] of entries) {
    const def = defRaw ?? {};
    if (def.showInModelSelector === false) continue;
    if (allowedIds && !allowedIds.has(id)) continue;
    if (!isStudioModelSupportedForProjectType(id, projectType)) continue;
    const modality = MODALITY_OF_CATEGORY[def.uiCategory ?? ''] ?? null;
    if (!modality) continue;
    rows[modality].push({
      id,
      name: def.name || id,
      provider: def.provider,
      modality,
      isNew: def.comparisonProfile?.lifecycle === 'experimental',
    });
  }
  return { image: groupFamilies(rows.image), video: groupFamilies(rows.video) };
};

const TABS: Array<{ id: StudioModelsModality; icon: React.ReactNode; label: string }> = [
  { id: 'image', icon: <ImageIcon size={15} strokeWidth={1.9} />, label: 'Image' },
  { id: 'video', icon: <Film size={15} strokeWidth={1.9} />, label: 'Video' },
];

const PANEL_STYLE: React.CSSProperties = { boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)' };
const BACKDROP_STYLE: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 1090 };

const ModalityTab: React.FC<{
  id: StudioModelsModality;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  isPinnedModality: boolean;
  onSelect: (tab: StudioModelsModality) => void;
}> = React.memo(({ id, icon, label, isActive, isPinnedModality, onSelect }) => {
  const handleClick = useCallback(() => onSelect(id), [onSelect, id]);
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      className={clsx(
        'relative flex h-8 flex-1 items-center justify-center gap-1.5 rounded-full transition-colors',
        isActive ? 'bg-white/[0.12] text-[#ECECEC]' : 'text-[#AFAFAF] hover:bg-white/[0.08] hover:text-[#ECECEC]',
      )}
    >
      {icon}
      {isPinnedModality && <span className="h-1.5 w-1.5 rounded-full bg-[#ECECEC]" />}
    </button>
  );
});
ModalityTab.displayName = 'ModalityTab';

const chipClassName = (active: boolean) => clsx(
  'h-6 rounded-full px-2 font-medium transition-colors',
  active ? 'bg-white/[0.12] text-[#ECECEC]' : 'text-[#AFAFAF] hover:bg-white/[0.08] hover:text-[#ECECEC]',
);
const CHIP_FONT_STYLE: React.CSSProperties = { fontSize: 12 };
const CHIP_GROUP_CLASS = 'flex items-center gap-0.5 rounded-full border border-white/[0.08] p-0.5';

const ResolutionChip: React.FC<{
  modelId: string;
  paramId: string;
  label: string;
  value: string | number;
  active: boolean;
  onPick: (modelId: string, paramId: string, value: string | number) => void;
}> = React.memo(({ modelId, paramId, label, value, active, onPick }) => {
  const handleClick = useCallback(() => onPick(modelId, paramId, value), [onPick, modelId, paramId, value]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className={chipClassName(active)}
      style={CHIP_FONT_STYLE}
    >
      {label}
    </button>
  );
});
ResolutionChip.displayName = 'ResolutionChip';

const VersionChip: React.FC<{
  modelId: string;
  label: string;
  name: string;
  active: boolean;
  onPinModel: (modelId: string) => void;
}> = React.memo(({ modelId, label, name, active, onPinModel }) => {
  const handleClick = useCallback(() => onPinModel(modelId), [onPinModel, modelId]);
  return (
    <button type="button" onClick={handleClick} aria-label={name} className={chipClassName(active)} style={CHIP_FONT_STYLE}>
      {label}
    </button>
  );
});
VersionChip.displayName = 'VersionChip';

const ModelRow: React.FC<{
  row: StudioModelRow;
  isPinned: boolean;
  activeVersionId: string | null;
  currentValue: unknown;
  onPinModel: (modelId: string) => void;
  onPickResolution: (modelId: string, paramId: string, value: string | number) => void;
}> = React.memo(({ row, isPinned, activeVersionId, currentValue, onPinModel, onPickResolution }) => {
  const chipModelId = activeVersionId ?? row.id;
  const chips = RESOLUTION_CHIPS[chipModelId];
  const handlePin = useCallback(() => onPinModel(activeVersionId ?? row.id), [onPinModel, activeVersionId, row.id]);
  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-2 rounded-[12px] px-3 py-2 transition-colors',
        isPinned ? 'bg-white/[0.12]' : 'hover:bg-white/[0.08]',
      )}
    >
      <button
        type="button"
        onClick={handlePin}
        // The Daimon clicks by exact name: Nano Banana 1 is called "Nano Banana", so that family's button must not be.
        aria-label={row.versions?.some((version) => version.name === row.name) ? `${row.name} family` : undefined}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
        title={isPinned ? 'Active model, click to return to Auto' : 'Make this the active model'}
      >
        <ProviderIcon modelId={row.id} provider={row.provider} />
        <span className="truncate text-[#ECECEC]" style={{ fontSize: 14 }}>
          {row.name}
        </span>
        {row.isNew && <span className="shrink-0 text-[#AFAFAF]" style={{ fontSize: 12 }}>New</span>}
      </button>
      <div className="flex shrink-0 items-center gap-2">
        {row.versions && (
          <div className={CHIP_GROUP_CLASS}>
            {row.versions.map((version) => (
              <VersionChip
                key={version.id}
                modelId={version.id}
                label={version.label}
                name={version.name}
                active={version.id === activeVersionId}
                onPinModel={onPinModel}
              />
            ))}
          </div>
        )}
        {chips && (
          <div className={CHIP_GROUP_CLASS}>
            {chips.choices.map((choice) => (
              <ResolutionChip
                key={choice.label}
                modelId={chipModelId}
                paramId={chips.paramId}
                label={choice.label}
                value={choice.value}
                active={currentValue === choice.value}
                onPick={onPickResolution}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
ModelRow.displayName = 'ModelRow';

export interface StudioModelsPopoverViewProps {
  rowsByModality: Record<StudioModelsModality, StudioModelRow[]>;
  tab: StudioModelsModality;
  onTabChange: (tab: StudioModelsModality) => void;
  pinnedModelId: string | null; // null = Auto routing
  pinnedModality: StudioModelsModality | null;
  resolutionOf: (modelId: string, paramId: string) => unknown;
  onPinModel: (modelId: string) => void;
  onPickResolution: (modelId: string, paramId: string, value: string | number) => void;
  onClose: () => void;
}

/** Pure panel visuals, kernel-free so the dev-preview route can render it for headless Chrome. */
export const StudioModelsPopoverView: React.FC<StudioModelsPopoverViewProps> = ({
  rowsByModality,
  tab,
  onTabChange,
  pinnedModelId,
  pinnedModality,
  resolutionOf,
  onPinModel,
  onPickResolution,
  onClose,
}) => {
  return (
    <div
      role="dialog"
      aria-label="Models"
      className="flex w-[480px] flex-col overflow-hidden rounded-[20px] border border-white/[0.06] bg-[#353535]"
      style={PANEL_STYLE}
    >
      {/* Header */}
      <div className="flex items-center justify-between pl-5 pr-3 pt-3">
        <div className="font-semibold text-[#ECECEC]" style={{ fontSize: 16 }}>Models</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-[#ECECEC]"
        >
          <X size={16} />
        </button>
      </div>
      <p className="px-5 pb-3 text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '18px' }}>
        With no model chosen, Studio picks the best one for each request.
      </p>

      {/* Modality tabs */}
      <div className="mx-3 mb-2 flex shrink-0 gap-1">
        {TABS.map((t) => (
          <ModalityTab
            key={t.id}
            id={t.id}
            icon={t.icon}
            label={t.label}
            isActive={tab === t.id}
            isPinnedModality={pinnedModality === t.id}
            onSelect={onTabChange}
          />
        ))}
      </div>

      <div className="custom-scrollbar max-h-[280px] overflow-y-auto px-1.5 pb-1.5">
        {rowsByModality[tab].map((row) => {
          const isPinned = rowHoldsModel(row, pinnedModelId);
          const activeVersionId = isPinned && row.versions ? pinnedModelId : null;
          const chipModelId = activeVersionId ?? row.id;
          const chips = RESOLUTION_CHIPS[chipModelId];
          return (
            <ModelRow
              key={row.id}
              row={row}
              isPinned={isPinned}
              activeVersionId={activeVersionId}
              currentValue={chips ? resolutionOf(chipModelId, chips.paramId) : undefined}
              onPinModel={onPinModel}
              onPickResolution={onPickResolution}
            />
          );
        })}
        {rowsByModality[tab].length === 0 && (
          <div className="px-3 py-6 text-center text-[#AFAFAF]" style={{ fontSize: 13 }}>No models here.</div>
        )}
      </div>

      {/* Footer */}
      {pinnedModelId ? (
        <div className="shrink-0 border-t border-white/[0.06] px-5 py-2.5 text-[#8E8E8E]" style={{ fontSize: 12 }}>
          The active model is used for every generation. Click it again to return to Auto.
        </div>
      ) : null}
    </div>
  );
};

/** Kernel-wired container: portal to document.body, fixed above the anchor, backdrop closes. */
export const StudioModelsPopover: React.FC<{
  anchorEl: HTMLElement | null;
  onClose: () => void;
}> = ({ anchorEl, onClose }) => {
  const { kernel } = useKernel();
  const projectType = useKernelStore((state) => {
    const session = state.activeChatSessionId
      ? state.inventory.ChatSession.registry[state.activeChatSessionId]
      : null;
    const projectId = session ? resolveProjectIdFromChatSessionContext(session) : null;
    return projectId ? state.inventory.Project.registry[projectId]?.projectType ?? null : null;
  });
  const diffusionModel = useKernelStore((s) => s.diffusionModel);
  const locked = useKernelStore((s) => s.userPreferences.lockSelectedModelForAgent === true);
  const modelParams = useKernelStore((s) => s.modelParams);
  const accessScope = useKernelStore((s) => s.userContext.permissions?.accessScope);
  const organisationRestricted = useKernelStore((s) => s.organisationCredits?.isRestricted ?? null);
  const suspensionOrganisationId = useKernelStore((s) => s.userContext.organisationId);
  const suspensionTeamId = useKernelStore((s) => s.userContext.teamId);
  const serverSuspension = useKernelStore((s) => s.userContext.accessSuspension);
  const modelVisibility = useKernelStore((s) => s.userPreferences.modelVisibility);
  const studioModelUsage = useKernelStore((s) => s.userContext.studioModelUsage);

  // Families the account cannot spend on (billing carve-out) or this server cannot run, on top of the scope gate.
  const withheldFamilies = useMemo(
    () => clientAccessSuspension(serverSuspension, suspensionOrganisationId, suspensionTeamId).withheldModelFamilies,
    [serverSuspension, suspensionOrganisationId, suspensionTeamId],
  );

  // Only models the user can ACTUALLY run (same gate as the classic ModelSelector), offering
  // a disabled/legacy model here once let it get pinned and deadlocked the agent.
  const activeModelIds = useMemo(
    () => new Set(getActiveSelectableStudioModelIds({ accessScope, organisationRestricted, withheldModelFamilies: withheldFamilies, modelVisibility: modelVisibility || {} })),
    [accessScope, organisationRestricted, withheldFamilies, modelVisibility],
  );
  // Access (runnability) is broader than list visibility: healing a pin must key on ACCESS,
  // a model the user merely hid from the list still runs fine and keeps its pin.
  const accessibleModelIds = useMemo(
    () => new Set(
      buildStudioFrontendModelAccessState({ accessScope, organisationRestricted, withheldModelFamilies: withheldFamilies, modelVisibility: modelVisibility || {} })
        .filter((state) => state.hasAccess)
        .map((state) => state.modelId),
    ),
    [accessScope, organisationRestricted, withheldFamilies, modelVisibility],
  );
  const compatibleAccessibleModelIds = useMemo(
    () => new Set([...accessibleModelIds].filter((modelId) => isStudioModelSupportedForProjectType(modelId, projectType))),
    [accessibleModelIds, projectType],
  );
  const rowsByModality = useMemo(() => {
    const rows = buildStudioModelRows(activeModelIds, projectType);
    const compareModels = compareStudioModels(studioModelUsage);
    for (const modalityRows of Object.values(rows)) modalityRows.sort((a, b) => compareModels(a.id, b.id));
    return rows;
  }, [activeModelIds, projectType, studioModelUsage]);
  const pinnedModelId = locked ? diffusionModel : null;
  const pinnedModality: StudioModelsModality | null = useMemo(() => {
    if (!pinnedModelId) return null;
    for (const m of ['image', 'video'] as StudioModelsModality[]) {
      if (rowsByModality[m].some((r) => rowHoldsModel(r, pinnedModelId))) return m;
    }
    return null;
  }, [pinnedModelId, rowsByModality]);

  const [tab, setTab] = useState<StudioModelsModality>(pinnedModality ?? 'image');

  // Declare avant l'effet d'auto-guerison ci-dessous : une fonction declaree apres n'aurait
  // pas suivi les changements de valeur qu'elle lit.
  const persistLock = useCallback((nextLocked: boolean, modelId?: string) => {
    const k = kernel.current;
    if (!k) return;
    // The pinned model is saved with the lock: a reload otherwise kept the lock on the default model.
    if (nextLocked && modelId) void k.updateAndSaveUserPreference('lockedStudioModel', modelId);
    void k.updateAndSaveUserPreference('lockSelectedModelForAgent', nextLocked);
  }, [kernel]);

  // Auto-heal: a pinned model that is no longer runnable (disabled/legacy/no access) would
  // deadlock the agent (the lock blocks SELECT_MODEL). Return to Auto the moment we see it.
  useEffect(() => {
    if (locked && diffusionModel && !compatibleAccessibleModelIds.has(diffusionModel)) {
      void kernel.current?.updateAndSaveUserPreference('lockSelectedModelForAgent', false);
    }
  }, [kernel, locked, diffusionModel, compatibleAccessibleModelIds]);

  // Fixed positioning above the anchor, clamped to the viewport; recomputed on resize.
  const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null);
  const reposition = useCallback(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 520));
    setPosition({ left, bottom: Math.max(8, window.innerHeight - rect.top + 8) });
  }, [anchorEl]);
  useEffect(() => {
    // Mesure du DOM, pas une synchronisation d'etat evitable : le panneau est portale sur
    // document.body et doit lire la position de son ancre pour se placer. Il n'existe pas de
    // source React de cette valeur, et la regle ne sait pas distinguer ce cas d'un effet en
    // cascade. Restructurer un positionnement deja valide en preprod couterait plus cher que
    // la dette qu'on retirerait ici.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    reposition();
    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [reposition]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ensureModelParams = useCallback((modelId: string) => {
    const k = kernel.current;
    if (!k) return;
    if (!k.getModelParams?.()?.[modelId]) {
      const getter = getDefaultParamsForModel(modelId);
      if (getter) k.setModelParams?.((prev) => ({ ...prev, [modelId]: getter() }));
    }
  }, [kernel]);

  // Le modele epingle est relu au clic via les getters kernel, pas capture en dependance :
  // ces deux handlers sont passes a des lignes memoisees, une reference qui change a chaque
  // epinglage les rerend toutes pour rien.
  const getPinnedModelId = useCallback((): string | null => {
    const k = kernel.current;
    if (!k) return null;
    return k.getUserPreferences()?.lockSelectedModelForAgent === true ? (k.getDiffusionModel() ?? null) : null;
  }, [kernel]);

  const pinModel = useCallback((modelId: string) => {
    const k = kernel.current;
    if (!k) return;
    if (getPinnedModelId() === modelId) {
      // Clicking the pinned model returns to Auto: the router picks per task again.
      persistLock(false);
      return;
    }
    ensureModelParams(modelId);
    k.setDiffusionModel?.(modelId);
    persistLock(true, modelId);
    k.logUserAction?.('Studio Models Popover Pin', { modelId });
  }, [kernel, getPinnedModelId, persistLock, ensureModelParams]);

  const pickResolution = useCallback((modelId: string, paramId: string, value: string | number) => {
    const k = kernel.current;
    if (!k) return;
    ensureModelParams(modelId);
    void k.setModelParameter(modelId, paramId, value);
    if (getPinnedModelId() !== modelId) {
      k.setDiffusionModel?.(modelId);
      persistLock(true, modelId);
    }
    k.logUserAction?.('Studio Models Popover Resolution', { modelId, paramId, value });
  }, [kernel, getPinnedModelId, persistLock, ensureModelParams]);

  const resolutionOf = useCallback(
    (modelId: string, paramId: string) => (modelParams?.[modelId] as Record<string, unknown> | undefined)?.[paramId],
    [modelParams],
  );

  const positionStyle = useMemo<React.CSSProperties | undefined>(
    () => (position ? { position: 'fixed', left: position.left, bottom: position.bottom, zIndex: 1100 } : undefined),
    [position],
  );

  if (typeof document === 'undefined' || !position) return null;

  return createPortal(
    <>
      {/* Click-away backdrop (transparent), the panel is portaled, so outside-click
          detection on the composer wrapper cannot see it. */}
      <div style={BACKDROP_STYLE} onMouseDown={onClose} />
      <div style={positionStyle}>
        <StudioModelsPopoverView
          rowsByModality={rowsByModality}
          tab={tab}
          onTabChange={setTab}
          pinnedModelId={pinnedModelId}
          pinnedModality={pinnedModality}
          resolutionOf={resolutionOf}
          onPinModel={pinModel}
          onPickResolution={pickResolution}
          onClose={onClose}
        />
      </div>
    </>,
    document.body,
  );
};

StudioModelsPopover.displayName = 'StudioModelsPopover';
